import React from 'react';

interface CustomCheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  value: string;
  checked: boolean;
  step?: number;
  totalSteps?: number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string | null | undefined;
  className?: string;
  textStyle?: string;
  labelStyle?: string;
  screen?: string;
  bgColor?: string;
  isActive?: boolean;
  hasSelection?: boolean;
}

const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
  name,
  value,
  checked,
  onChange,
  isActive = true,
  label,
  step = 0,
  totalSteps = 3,
  className,
  textStyle,
  hasSelection = false,
  labelStyle,
  screen = '',
  bgColor = '#ffffff',
  ...props
}) => {
  const size = 26;
  const strokeWidth = 7;
  const innerStrokeWidth = 2;
  const radius = (size - strokeWidth) / 2;
  const innerRadius = size * 0.39;
  const center = size / 2;

  const hideOuterRing = !isActive && !checked && step === 0;
  const isCheckedState = checked;
  const getSegmentColor = (i: number) => {
    if (isCheckedState || i < step) {
      return '#113E53';
    }
    return '#B3B3B2';
  };

  const stepAngle = 360 / totalSteps;
  const gapAngle = 22;
  const visibleAngle = stepAngle - gapAngle;

  const circumference = 2 * Math.PI * radius;
  const visibleLength = (visibleAngle / 360) * circumference;
  const gapLength = circumference;

  return (
    <div
      className={`relative w-[26px] h-[26px] flex items-center justify-center ${className}`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="absolute top-0 left-0"
      >
        {!hideOuterRing &&
          Array.from({ length: totalSteps }).map((_, i) => (
            <circle
              key={`${i}-${step}-${checked}`}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={getSegmentColor(i)}
              strokeWidth={strokeWidth}
              strokeDasharray={`${visibleLength} ${gapLength}`}
              transform={`rotate(${i * stepAngle + 180} ${center} ${center})`}
              vectorEffect="non-scaling-stroke"
              shapeRendering="geometricPrecision"
            />
          ))}

        <circle
          cx={center}
          cy={center}
          r={innerRadius}
          fill={
            checked
              ? '#113E53'
              : (isActive && !checked) || step > 0
                ? '#b3b3b2'
                : bgColor
          }
          stroke={checked || step > 0 || isActive ? 'white' : '#B3B3B2'}
          strokeWidth={innerStrokeWidth}
          vectorEffect="non-scaling-stroke"
          shapeRendering="geometricPrecision"
        />
        {checked && (
          <path
            d="M8.5 13.5 L11.5 16.5 L17.5 10"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>

      <input
        {...props}
        type="checkbox"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="absolute inset-0 opacity-0 cursor-pointer z-10"
      />
    </div>
  );
};

export default CustomCheckbox;
